// src/components/PvPLobby.jsx
import React, { useEffect, useState, useRef } from "react";
import { db } from "../utils/firebase";
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  query,
  where,
  updateDoc,
  getDoc,
  getDocs,
} from "firebase/firestore";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/AuthenticationProvider";
import { v4 as uuidv4 } from "uuid";
import { Users, Clock, CheckCircle, XCircle, Bell, Info } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const PvPLobby = ({ game, onMatchCreated, onLeaveLobby, gamesData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, organizationId } = useAuth();
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sentInvites, setSentInvites] = useState([]);
  const [notification, setNotification] = useState(null);

  // Use refs to track active match
  const activeMatchRef = useRef(null);

  // Map of game IDs to multiplayer routes
  const gameIdToMultiRouteMap = {
    nback: "/memorymatrixmulti",
    "color-clash": "/colormulti",
    "information-processing": "/datastreammulti",
    "visual-search": "/focusfindermulti",
    "math-race": "/mathracemulti",
    "set-shifting": "/ruleshiftermulti",
    "stop-signal": "/stopsignalmulti",
    "inspection-time": "/inspectiontimemulti",
    "span-task": "/spantaskmulti",
    "search-task": "/visualsearchmulti",
  };

  // Get game name for better display
  const gameName =
    gamesData?.find((g) => g.id === game)?.name ||
    game?.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

  useEffect(() => {
    if (!organizationId || !user?.email) return;

    console.log("PvPLobby initialized for game:", game);

    // First, update user's status in the general lobby
    const registerInLobby = async () => {
      const lobbyRef = doc(
        db,
        `organizations/${organizationId}/pvpLobby/${user.email}`
      );

      await setDoc(lobbyRef, {
        userId: user.email,
        userName: user.firstName
          ? `${user.firstName} ${user.lastName || ""}`.trim()
          : user.email.split("@")[0],
        lastActive: serverTimestamp(),
      });

      console.log("User registered in lobby:", user.email);
    };

    registerInLobby();

    // Cleanup function to remove from lobby when component unmounts
    const cleanup = async () => {
      try {
        const lobbyRef = doc(
          db,
          `organizations/${organizationId}/pvpLobby/${user.email}`
        );
        await deleteDoc(lobbyRef);
        console.log("User removed from lobby:", user.email);
      } catch (error) {
        console.error("Error cleaning up lobby:", error);
      }
    };

    // Listen for all available players in pvpLobby
    const lobbyQuery = query(
      collection(db, `organizations/${organizationId}/pvpLobby`)
    );

    const unsubscribeLobby = onSnapshot(lobbyQuery, (snapshot) => {
      // Filter the current user out in memory after getting results
      const players = snapshot.docs
        .map((doc) => doc.data())
        .filter((p) => p.userId !== user.email);

      console.log("Found players in lobby:", players.length);
      console.log("Players:", players);

      setAvailablePlayers(players);
      setIsLoading(false);
    });

    // Listen for ALL matches that you're a part of (active or pending)
    const matchesQuery = query(
      collection(db, `organizations/${organizationId}/pvpMatches`),
      where("players", "array-contains", user.email),
      where("game", "==", game)
    );

    const unsubscribeMatches = onSnapshot(matchesQuery, (snapshot) => {
      // Process all matches
      const allMatches = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // If we have an active match already being tracked
      if (activeMatchRef.current) {
        // Check if it's in the latest snapshot
        const activeMatch = allMatches.find(
          (match) =>
            match.id === activeMatchRef.current &&
            match.gameState?.status === "active"
        );

        if (activeMatch) {
          console.log(
            "Active match found, redirecting to game:",
            activeMatch.id
          );
          onMatchCreated(activeMatch.id);
          return; // Return early as we're redirecting
        } else {
          // Active match no longer exists or is no longer active
          activeMatchRef.current = null;
        }
      }

      // Filter matches into pending invites (received) and sent invites
      const pendingInvs = allMatches.filter(
        (match) =>
          match.gameState?.status === "pending" && !match.accepted[user.email]
      );

      const sentInvs = allMatches.filter(
        (match) =>
          match.gameState?.status === "pending" && match.accepted[user.email]
      );

      // Check for any matches that just became active
      const activeMatches = allMatches.filter(
        (match) =>
          match.gameState?.status === "active" &&
          match.accepted[user.email] === true
      );

      if (activeMatches.length > 0 && !activeMatchRef.current) {
        // We have a newly active match!
        const newActiveMatch = activeMatches[0];
        console.log("New active match detected:", newActiveMatch.id);
        activeMatchRef.current = newActiveMatch.id;

        // Get the corresponding route for this game
        const multiplayerRoute = gameIdToMultiRouteMap[newActiveMatch.game];

        onMatchCreated(newActiveMatch.id);

        return; // Return early as we're redirecting
      }

      // Update states with filtered matches
      setPendingInvites(pendingInvs);
      setSentInvites(sentInvs);

      // Show notification for new invites
      if (
        pendingInvs.length > 0 &&
        pendingInvites.length < pendingInvs.length
      ) {
        const newestInvite = pendingInvs[0];
        const opponentEmail = newestInvite.players.find(
          (p) => p !== user.email
        );
        const opponentName =
          newestInvite.playerNames?.[opponentEmail] ||
          opponentEmail.split("@")[0];
        const gameName =
          newestInvite.gameName ||
          newestInvite.game
            .replace(/-/g, " ")
            .replace(/\b\w/g, (l) => l.toUpperCase());

        setNotification({
          type: "invite",
          message: `${opponentName} has invited you to play ${gameName}!`,
          matchId: newestInvite.id,
        });
      }
    });

    // Set up a periodic update to keep the user's presence fresh
    const activityInterval = setInterval(() => {
      const updateActivity = async () => {
        try {
          const lobbyRef = doc(
            db,
            `organizations/${organizationId}/pvpLobby/${user.email}`
          );

          await updateDoc(lobbyRef, {
            lastActive: serverTimestamp(),
          });
        } catch (error) {
          console.error("Error updating user activity:", error);
          // If update fails (doc may have been deleted), try to recreate it
          registerInLobby();
        }
      };

      updateActivity();
    }, 30000); // Update every 30 seconds

    return () => {
      clearInterval(activityInterval);
      unsubscribeLobby();
      unsubscribeMatches();
      cleanup();
    };
  }, [
    organizationId,
    user?.email,
    game,
    pendingInvites.length,
    onMatchCreated,
    location.pathname,
  ]);

  const initiateMatch = async (opponentEmail, opponentDisplayName) => {
    const matchId = uuidv4();

    await setDoc(
      doc(db, `organizations/${organizationId}/pvpMatches/${matchId}`),
      {
        matchId,
        players: [user.email, opponentEmail],
        playerNames: {
          [user.email]: user.firstName
            ? `${user.firstName} ${user.lastName || ""}`.trim()
            : user.email.split("@")[0],
          [opponentEmail]: opponentDisplayName || opponentEmail.split("@")[0],
        },
        accepted: {
          [user.email]: true,
          [opponentEmail]: false,
        },
        game,
        gameName,
        gameState: {
          status: "pending",
          scores: {
            [user.email]: 0,
            [opponentEmail]: 0,
          },
          progress: {},
        },
        chatEnabled: true,
        startTime: null,
        endTime: null,
        timestamp: serverTimestamp(),
      }
    );

    // Notify the user that the invite was sent
    setNotification({
      type: "sent",
      message: `Invite sent to ${
        opponentDisplayName || opponentEmail.split("@")[0]
      } for ${gameName}!`,
      matchId: matchId,
    });
  };

  const cancelInvite = async (matchId) => {
    try {
      const matchRef = doc(
        db,
        `organizations/${organizationId}/pvpMatches/${matchId}`
      );
      await deleteDoc(matchRef);
    } catch (error) {
      console.error("Error canceling invite:", error);
    }
  };

  const respondToInvite = async (matchId, accept) => {
    const matchRef = doc(
      db,
      `organizations/${organizationId}/pvpMatches/${matchId}`
    );

    if (accept) {
      try {
        // First get the current match data
        const matchDoc = await getDoc(matchRef);
        if (!matchDoc.exists()) {
          console.error("Match not found");
          return;
        }

        const matchData = matchDoc.data();
        const gameId = matchData.game;

        // Mark this match as the active one
        activeMatchRef.current = matchId;

        // Update the match to show you've accepted
        await updateDoc(matchRef, {
          accepted: {
            ...matchData.accepted,
            [user.email]: true,
          },
          startTime: serverTimestamp(),
          "gameState.status": "active", // This will trigger the other player's listener
        });

        // Get the corresponding route for this game
        const multiplayerRoute = gameIdToMultiRouteMap[gameId];

        onMatchCreated(matchId);
      } catch (error) {
        console.error("Error accepting invite:", error);
      }
    } else {
      // Decline by deleting the match
      try {
        await deleteDoc(matchRef);
      } catch (error) {
        console.error("Error declining invite:", error);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <div className="animate-spin h-8 w-8 border-4 border-emerald-500 rounded-full border-t-transparent mb-4"></div>
        <p className="text-slate-300">Loading multiplayer lobby...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Users size={20} className="text-emerald-400" />
          {gameName} - Multiplayer Lobby
        </h2>

        {pendingInvites.length > 0 && (
          <div className="mb-6 bg-slate-800 border border-emerald-700 rounded-lg p-4">
            <h3 className="text-lg font-medium text-white mb-3">
              Game Invitations
            </h3>
            <div className="space-y-3">
              {pendingInvites.map((invite) => {
                const opponent = invite.players.find((p) => p !== user.email);
                const opponentName =
                  invite.playerNames?.[opponent] || opponent.split("@")[0];
                const inviteGameName =
                  invite.gameName ||
                  invite.game
                    .replace(/-/g, " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase());

                return (
                  <div
                    key={invite.id}
                    className="flex items-center justify-between bg-slate-700/50 p-3 rounded-lg"
                  >
                    <div>
                      <p className="text-white font-medium">
                        {opponentName} has invited you
                      </p>
                      <p className="text-sm text-slate-300">
                        Game: {inviteGameName}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => respondToInvite(invite.id, true)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-md flex items-center gap-1 text-sm"
                      >
                        <CheckCircle size={16} />
                        Accept
                      </button>
                      <button
                        onClick={() => respondToInvite(invite.id, false)}
                        className="bg-slate-600 hover:bg-slate-700 text-white px-3 py-1.5 rounded-md flex items-center gap-1 text-sm"
                      >
                        <XCircle size={16} />
                        Decline
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {sentInvites.length > 0 && (
          <div className="mb-6 bg-slate-800 border border-slate-700 rounded-lg p-4">
            <h3 className="text-lg font-medium text-white mb-3">
              Pending Invitations
            </h3>
            <div className="space-y-3">
              {sentInvites.map((invite) => {
                const opponent = invite.players.find((p) => p !== user.email);
                const opponentName =
                  invite.playerNames?.[opponent] || opponent.split("@")[0];
                return (
                  <div
                    key={invite.id}
                    className="flex items-center justify-between bg-slate-700/50 p-3 rounded-lg"
                  >
                    <div>
                      <p className="text-white font-medium">
                        Waiting for {opponentName} to respond
                      </p>
                      <div className="flex items-center gap-1 text-xs text-blue-300 mt-1">
                        <Clock size={12} />
                        <span>Invite pending...</span>
                      </div>
                    </div>
                    <button
                      onClick={() => cancelInvite(invite.id)}
                      className="bg-slate-600 hover:bg-slate-700 px-3 py-1.5 rounded-md text-white text-sm transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {availablePlayers.length > 0 ? (
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
            <h3 className="text-lg font-medium text-white mb-3 flex items-center gap-2">
              <Users size={18} />
              Online Players ({availablePlayers.length})
              <div className="relative group">
                <Info size={16} className="text-slate-400 cursor-help" />
                <div className="absolute left-0 bottom-full mb-2 w-64 bg-slate-800 p-2 rounded text-xs text-slate-300 hidden group-hover:block shadow-lg border border-slate-700">
                  All online players are shown here. You can invite anyone to
                  play {gameName}.
                </div>
              </div>
            </h3>
            <div className="space-y-2">
              {availablePlayers.map((player) => {
                return (
                  <div
                    key={player.userId}
                    className="flex justify-between items-center bg-slate-700/50 p-3 rounded-lg"
                  >
                    <div>
                      <span className="text-white font-medium">
                        {player.userName || player.userId.split("@")[0]}
                      </span>
                      <div className="flex items-center gap-1 text-xs text-emerald-300 mt-1">
                        <Clock size={12} />
                        <span>Online</span>
                      </div>
                    </div>
                    <button
                      onClick={() =>
                        initiateMatch(player.userId, player.userName)
                      }
                      className="bg-emerald-600 hover:bg-emerald-700 px-3 py-1.5 rounded-md text-white text-sm transition-colors"
                    >
                      Invite to Play
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 text-center">
            <p className="text-slate-300 mb-2">
              No players available right now
            </p>
            <p className="text-sm text-slate-400">
              Wait for someone to join, or invite a friend to play!
            </p>
          </div>
        )}
      </div>

      <div className="flex justify-between pt-4 border-t border-slate-700 mt-2">
        <button
          onClick={onLeaveLobby}
          className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg transition-colors"
        >
          Leave Lobby
        </button>
      </div>

      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-4 right-4 bg-emerald-600 text-white p-4 rounded-lg shadow-lg z-50 flex items-center gap-3"
          >
            <Bell className="text-white" size={20} />
            <div>
              <p className="font-medium">{notification.message}</p>
              {notification.type === "invite" && (
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => {
                      respondToInvite(notification.matchId, true);
                      setNotification(null);
                    }}
                    className="px-3 py-1 bg-white text-emerald-700 rounded-md text-sm font-medium"
                  >
                    Accept
                  </button>
                  <button
                    onClick={() => {
                      respondToInvite(notification.matchId, false);
                      setNotification(null);
                    }}
                    className="px-3 py-1 bg-emerald-800 text-white rounded-md text-sm"
                  >
                    Decline
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={() => setNotification(null)}
              className="absolute top-2 right-2 text-emerald-200 hover:text-white"
            >
              <XCircle size={16} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PvPLobby;
